import {ArchiveConstants, slideStatusEnum} from "./const";
import {daysToMilliseconds, getAWSCloudFrontUrl, getS3Url, getStateAwareUrl, isCloud, joinPath, slideStatusCategory} from "./utils";
import {getLastRetrievedDate} from "./archival_utils";
import {get, get_response, patch} from "../helper/request";
import {error_manager} from "../helper/error_manager";
import {AxiosConfig} from "../helper/axios.config";
import {isDeviceIPRemote} from "./device_utils";
import axios from "axios";
import {aclPermissions, hasPermissions} from "../const/ACLPermissions";
import {getStitchManifestFilename, parseStitchManifest} from "../component/gammaviewer/openseadragon/utils/stitchManifest";

export const patchSlide = slide =>
	patch(`/api/slide/${slide.id}/`, slide)

export const getSlideDateTime = slide => new Date(slide.date_time)

export const getSlideArchivalDate = slide =>
	slide.archive_status < ArchiveConstants.STATUS.ARCHIVED ?
		new Date(getSlideDateTime(slide).getTime() + daysToMilliseconds(ArchiveConstants.FRESH_ARCHIVE_DAYS)) :
		slide.archive_status === ArchiveConstants.STATUS.RETRIEVED ?
			new Date(getLastRetrievedDate(slide.archival_logs).getTime() +
				daysToMilliseconds(ArchiveConstants.RE_ARCHIVE_DAYS)) : undefined;

export const pollForStitchingStatus = (slide_id, timer, max_txty_time, set_state, set_timer) => {
	set_state({
		isFetching: true,
	});

	let url = "/server/slide_status/?id=" + slide_id;
	get(url).then(data => {
			let json_data = JSON.parse(data);
			if (json_data.status === 0) {
				set_state({
					scan_ongoing: true,
					stitching_done: false,
					isFetching: false,
				});
			}
			if (slideStatusCategory(json_data.status) === slideStatusEnum.VIEWER_READY) {
				set_state({
					stitching_done: true,
					scan_ongoing: false,
					isFetching: false,
				});
				clearInterval(timer);
				timer = null;
			} else {
				let txty_time = parseFloat(json_data.txty_time) / 60;
				max_txty_time = Math.max(max_txty_time, txty_time);
				let stitcher_percent = json_data.stitcher_percent;
				let stitcher_time = json_data.stitcher_estimated_time / 60000;
				if (stitcher_time === 0) {
					stitcher_time = max_txty_time;
				}
				let final_percent = parseFloat(stitcher_percent).toFixed(2);
				let final_time = stitcher_time.toFixed(2);
				let time_left = Date.now() + final_time * 60 * 1000;
				set_state({
					stitch_percent: final_percent,
					stitch_time: time_left,
					stitching_done: false,
					isFetching: false,
				});
			}
			set_timer({
				timer,
				max_txty_time
			})
		})
		.catch((err) => {
			error_manager("Failed Getting Slide Status", err)
		});
};

export const getBaseSlidePath = (slide) => {
	if (isCloud()) {
		if (slide.is_secure) {
			return getAWSCloudFrontUrl(slide) + slide.path;
		}
		return joinPath(getS3Url(slide), slide.path);
	} else {
		const device_url =  isDeviceIPRemote() ? `${slide.device_ip}` : `/`
		const loc = slideStatusCategory(slide.status) === slideStatusEnum.VIEWER_READY ? `scan-hdd/${slide.scandrive_id}` : "scan-ssd"
		return joinPath(device_url, loc, slide.bucket_name, slide.path)
	}
};

export const getSlideTXTY = async (smallest_tiled_level, base_path) => {
	if (smallest_tiled_level === -1) {
		return {}
	}
	let res;
	const is_cloud = isCloud()
	let is_success = false;
	const file_loc = joinPath(base_path, "debug", "stitching", "log.json")
	try {
		if (is_cloud) {
			// Don't use stateAwareUrl for cloud,as it generates a s3 url
			res = await axios.get(file_loc, { withCredentials: true })
		} else {
			res = await get_response(file_loc)
		}
		is_success = true;
	} catch (err) {
		if (err?.response?.status === 404) {
			try{
				const file_loc = joinPath(base_path, "debug", "stitching", "img-log-.json")
				if (is_cloud) {
					// Don't use stateAwareUrl for cloud,as it generates a s3 url
					res = await axios.get(file_loc, { withCredentials: true })
				} else {
					res = await get_response(file_loc)
				}
				is_success = true;
			} catch (err) {
				if (err?.response?.status === 404) {
					console.log("failed to get TXTY data")
				}
			}
		}
	}

	let txty = {}
	if (!is_success) {
		return txty;
	}

	res = res.data
	if ('map' in res){
		const image_info = res['map']['stitching_info']['map']
		Object.keys(image_info).forEach(image => {
			const image_name = image.split(".")[0]
			const xIndexStart = image_name.indexOf('x');
			const yIndexStart = image_name.indexOf('y');
			const x_index = image_name.slice(xIndexStart+1, yIndexStart)
			const y_index = image_name.slice(yIndexStart+1)
			const abs_pos = image_info[image]['map']['Absolute Position']['map']
			txty[image] = {
				"pos": abs_pos,
				"x_index": x_index,
				"y_index": y_index
			}
		})
	} else {
		// New stitching logic
		res = res['stitching_info']
		Object.keys(res).forEach(image => {
			const image_name = image.split(".")[0]
			const xIndexStart = image_name.indexOf('x');
			const yIndexStart = image_name.indexOf('y');
			const x_index = image_name.slice(xIndexStart+1, yIndexStart)
			const y_index = image_name.slice(yIndexStart+1)
			const abs_pos = {
				'X': res[image]['absoluteX'],
				'Y': res[image]['absoluteY'],
				'displayOrder': res[image]['displayOrder'],
			}
			txty[image] = {
				"pos": abs_pos,
				"x_index": x_index,
				"y_index": y_index
			}
		})
	}
	return txty;
}

export const getSlideBackgroundColor = async (base_path) => {
	let bgColor = [255, 255, 255];
	const is_cloud = isCloud()
	const file_loc = joinPath(base_path, "meta", "scan_params.json")
	try {
		let res;
		if (is_cloud) {
			// Don't use stateAwareUrl for cloud,as it generates a s3 url
			res = await axios.get(file_loc, { withCredentials: true })
		} else {
			res = await get_response(file_loc)
		}
		if ('backgroundColor' in res.data) {
			const rgb = res.data['backgroundColor']?.split("_").map(Number).slice(0, 3)
			bgColor = rgb;
		}
	} catch (err) {
		if (err?.response?.status === 404) {
			try{
				let res;
				const file_loc = joinPath(base_path, "res", "stitch_dynamic_params.json")
				if (is_cloud) {
					// Don't use stateAwareUrl for cloud,as it generates a s3 url
					res = await axios.get(file_loc, { withCredentials: true })
				} else {
					res = await get_response(file_loc)
				}
				if ('blank_bgr' in res.data) {
					bgColor = res.data['blank_bgr']
				}
			} catch (err) {
				if (err?.response?.status === 404) {
					console.log("failed to get background color data")
				}
			}
		}
	}
	return bgColor;
}

export const getSlideMeta = async (zstack, base_path) => {
	let meta = {}
	if (!hasPermissions([aclPermissions.view_slide_details]) && !zstack) {
		return meta
	}
	const is_cloud = isCloud()
	const file_loc = joinPath(base_path, "meta", "slide_meta.json")
	try {
		let res;
		if (is_cloud) {
			// Don't use stateAwareUrl for cloud,as it generates a s3 url
			res = await axios.get(file_loc, { withCredentials: true })
		} else {
			res = await get_response(file_loc)
		}
		meta = res.data;
	} catch (err) {
		if (err?.response?.status === 404) {
			console.log("failed to get slide meta data")
		}
	}
	return meta;
}

/** Resolves to the parsed manifest, or null when the slide has none (or it is unreadable). */
export const getSlideStitchManifest = async (slide, base_path, bypassCache = false) => {
	if (!slide?.morphle_id) {
		return null
	}
	const is_cloud = isCloud()
	let file_loc = joinPath(base_path, "res", getStitchManifestFilename(slide.morphle_id))
	if (bypassCache) {
		file_loc += `?v=${Date.now()}`
	}
	try {
		let res;
		if (is_cloud) {
			// Don't use stateAwareUrl for cloud,as it generates a s3 url
			res = await axios.get(file_loc, { withCredentials: true, responseType: 'arraybuffer' })
		} else {
			res = await axios.get(getStateAwareUrl(file_loc), { ...AxiosConfig(), responseType: 'arraybuffer' })
		}
		return parseStitchManifest(res.data)
	} catch (err) {
		return null
	}
}
